import { useMemo } from 'react';

import type { Path} from 'react-hook-form';
import { useFormContext } from 'react-hook-form';

import { useAppSelector } from 'src/app/store/hooks';
import type { RootState } from 'src/app/store/types';
import type { PurposeEnum } from 'src/common/external/bambi-api/bambiApi';
import { FormControlledTripAddressField } from 'src/common/TripAddressField/FormControlledTripAddressField';
import {
  useAddressAutoFill,
  useAddressData,
  useAddressOptionRenderer,
} from 'src/common/TripAddressField/hooks';
import { useIsSelectedTripInThePast } from 'src/features/add-trip/useIsSelectedTripInThePast';

import useSetPickupAddress from './useSetPickupAddress';

import type { AddTripFormValues } from '../FormValues';

export function PickupAddressContextField({ index }: { index: number }) {
  return (
    <TripAddressContextField
      index={index}
      fieldName="pickupLocation"
      label="Pickup Address *"
      purpose="pickup"
      autoSetPickupAddress
    />
  );
}

export function DropoffAddressContextField({ index }: { index: number }) {
  return (
    <TripAddressContextField
      index={index}
      fieldName="dropoffLocation"
      label="Dropoff Address *"
      purpose="dropoff"
    />
  );
}

export interface TripAddressContextFieldProps {
  index: number; // index of the trip in the trips array, as the form can have multiple trip legs
  fieldName: 'pickupLocation' | 'dropoffLocation';
  label: string;
  purpose: PurposeEnum;
  autoSetPickupAddress?: boolean;
}

export function TripAddressContextField({
  index,
  fieldName,
  label,
  purpose,
  autoSetPickupAddress = false,
}: TripAddressContextFieldProps) {
  const isSelectedTripInThePast = useIsSelectedTripInThePast();
  const { getValues, setValue, watch } = useFormContext<AddTripFormValues>();
  const selectedPassenger = useAppSelector(
    (state: RootState) => state.addATrip.selectedPassenger
  );
  const selectedPayer = useAppSelector(
    (state: RootState) => state.addATrip.selectedPayer
  );

  const { trips } = getValues();

  const { recentAddressResponse, additionalResultsQueryFn } = useAddressData({
    selectedPassenger,
    selectedPayer,
  });

  const addressDetailsFieldName: Path<AddTripFormValues> = `trips.${index}.${fieldName}.address_details`;
  const driverNotesFieldName: Path<AddTripFormValues> = `trips.${index}.${fieldName}.driver_notes`;

  const { handleLocationChange } = useAddressAutoFill<AddTripFormValues>({
    recentAddressResponse,
    setValue,
    addressDetailsFieldName,
    driverNotesFieldName,
  });

  const { renderOption } = useAddressOptionRenderer(recentAddressResponse);

  useSetPickupAddress({
    index,
    trips,
    recentAddressResponse,
    setValue,
    enabled: autoSetPickupAddress,
  });

  const trip = useMemo(() => {
    // We have other code to prevent this, but just in case
    if (index >= trips.length) {
      console.error('TripAddressContextField: index out of bounds');
      return null;
    }
    return trips[index];
  }, [trips, index]);
  if (!trip) {
    return null;
  }

  const currentAddress = watch(`trips.${index}.${fieldName}.address`);

  return (
    <FormControlledTripAddressField
      name={`trips.${index}.${fieldName}.address`}
      label={label}
      value={currentAddress}
      onLocationChange={handleLocationChange}
      additionalResultsQueryFn={additionalResultsQueryFn}
      renderOption={renderOption}
      disabled={isSelectedTripInThePast}
      purpose={purpose}
    />
  );
}
